import { TechnologyItem } from '@tribal-worldwide-london/fa-product-service-vwg';
import { VideoModal } from '@tribal-worldwide-london/ui-component-library-vwg';
import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
  useTheme,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { HeartFilled } from '@volkswagen-onehub/icons-core';
import { useState } from 'react';
import {
  handleVideoEnded,
  handleVideoMilestone,
  handleVideoPause,
  handleVideoPlay,
} from '@/analytics/eventHelpers/video';
import { DisclaimerText } from '@/styles';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import Box from '@/modules/components/Box/Box';
import { CTACardProps } from '@/modules/components/CTACard/CTACard';
import PageSection from '@/modules/components/PageSection/PageSection';
import { NAVIGATION_HEIGHT } from '@/modules/constants/navigation';
import FeaturesCarousel from '@/modules/features/components/FeaturesCarousel/FeaturesCarousel';
import {
  getCTACardSlidesPerPage,
  getCarOptionsData,
  getSelectedVideo,
  getSlidesPerPage,
} from '@/modules/utils/storeUtils';
import { getImageUrl } from '@/modules/utils/themeUtils';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import CTACardCarousel from '@/modules/features/single/CTACardCarousel/CTACardCarousel';
import Mask from '@/modules/components/Mask/Mask';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { isMobile } from '@/modules/utils/breakpoints';

function LikedItems({
  likedItemsCodes,
  activeConfiguration,
}: {
  likedItemsCodes: string[];
  activeConfiguration: GlobalStoreStateConfiguration;
}) {
  const { contentManager, configManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const { retailers } = useGlobalStore((state) => state.app);
  const [videoId, setVideoId] = useState('');
  const isMobileWidth = useResizeResponsive(isMobile);

  const likedItemsData = likedItemsCodes
    .map((code: string) => getCarOptionsData(activeConfiguration, code, 'pdb'))
    .filter((item) => item);

  const standardOptionalVideosIds = [
    ...(activeConfiguration?.productData?.technologyItems?.extras ?? []),
    ...(activeConfiguration?.productData?.technologyItems?.standard ?? []),
  ].map((item) => item.id);

  let likedVideos = activeConfiguration?.productData?.technologyItems?.liked;
  likedVideos = likedVideos?.filter(
    (video) => !standardOptionalVideosIds.includes(video.id)
  );

  const videosCTACards =
    (
      likedVideos?.map((item: TechnologyItem) => {
        const videoUrl = item.assetCollection?.[0]?.url;
        if (!videoUrl) return;

        return {
          title: item.name,
          text: item.subtitle,
          image: {
            desktopUrl: getImageUrl(
              configManager,
              item.imageFileName,
              'apis.assetsUrl'
            ),
          },
          isVideo: true,
          onClick: () => setVideoId(videoUrl || ''),
        };
      }) as CTACardProps[]
    )?.filter((video) => video) || [];

  const { slidesPerPage: slidesLikedItems, hideNavigation: hideNavLikedItems } =
    getSlidesPerPage(likedItemsData.length, 3);

  const {
    slidesPerPage: slidesTechnologyVideos,
    hideNavigation: hideNavTechnologyVideos,
  } = getCTACardSlidesPerPage(videosCTACards.length);

  const analyticsCommonFields = {
    sectionName: SectionNames.SINGLE_PAGE_LIKED_ITEMS,
    configuration: activeConfiguration,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  const selectedVideoDetails = getSelectedVideo(likedVideos || [], videoId);

  const title = selectedVideoDetails?.name || null;

  const theme = useTheme();

  return (
    <>
      <PageSection>
        <Box
          display="flex"
          flexDirection="column"
          gap={DesignTokenSizeEnum.static150}
          b960={{
            gap: DesignTokenSizeEnum.static250,
            mb: DesignTokenSizeEnum.static300,
          }}
        >
          <Box display="flex" gap="static150" alignItems="center">
            <Text tag={TextTag.h2} appearance={TokenTextAppearance.headline300} bold>
              {contentManager.get('liked-items-section-title-normal')}
                {' '}
                {contentManager.get('liked-items-section-title-bold')}             
            </Text>
            <Mask
              iconDimension={
                isMobileWidth ? theme.size.static370 : theme.size.static450
              }
              background={theme.colors.signal.error}
            >
              <Box
                as={HeartFilled}
                color={theme.colors.background.primary}
                variant={isMobileWidth ? 'small' : 'default'}
              />
            </Mask>
          </Box>
          <Text>{contentManager.get('liked-items-section-subtitle')}</Text>
        </Box>
        {likedItemsData.length > 0 && (
          <FeaturesCarousel
            id="liked-items-carousel"
            ariaLabel="Liked items carousel"
            slidesPerPage={slidesLikedItems}
            featuresData={likedItemsData}
            hideNavigationDesktop={hideNavLikedItems}
            footerStacked
            sectionName={SectionNames.SINGLE_PAGE_LIKED_ITEMS}
            decoratorImgIcon={
              <Box as={HeartFilled} color={theme.colors.signal.error} />
            }
          />
        )}

        <DisclaimerText>
          <Text appearance={TokenTextAppearance.label100} color={TokenTextColor.tertiary}>
            {contentManager.get('prices-disclaimer-text')}
          </Text>
        </DisclaimerText>
      </PageSection>

      {videosCTACards.length > 0 && (
        <>
          <Box
            display="flex"
            flexDirection="column"
            gap={DesignTokenSizeEnum.static250}
            mb={DesignTokenSizeEnum.static350}
            b960={{ mb: DesignTokenSizeEnum.static515 }}
          >
            <Text
              tag={TextTag.h3}
              appearance={TokenTextAppearance.headline300}
              bold
            >
              {contentManager.get('liked-items-video-section-title')}
            </Text>
          </Box>
          <CTACardCarousel
            bulletPrefix="Liked items"
            ctaCardData={videosCTACards}
            id="liked-items-video-carousel"
            ariaLabel="Liked items video carousel"
            slidesPerPage={slidesTechnologyVideos}
            hideNavigationDesktop={hideNavTechnologyVideos}
          />
        </>
      )}
      <VideoModal
        isOpen={!!videoId}
        handleClose={() => setVideoId('')}
        videoId={videoId}
        goBackLabel={contentManager.get('ctas-go-back')}
        playerName="youtube-video-embed"
        autoplay
        controls
        navigationHeight={NAVIGATION_HEIGHT}
        videoPlayCallback={(fields) => {
          handleVideoPlay(analyticsCommonFields, {
            ...fields,
            title,
          });
        }}
        videoPauseCallback={(fields) =>
          handleVideoPause(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
        videoEndedCallback={(fields) =>
          handleVideoEnded(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
        videoMilestoneCallback={(fields) =>
          handleVideoMilestone(analyticsCommonFields, {
            ...fields,
            title: selectedVideoDetails?.name,
          })
        }
      />
    </>
  );
}

export default LikedItems;
